/**
 * TeamAccessWrapper.js
 * Guards routes and ensures user has team access before showing content
 */

import React, { useState } from "react";
import { useTeam } from "../context/TeamContext";
import { AuthContext } from "../../AuthProvider";

export default function TeamAccessWrapper({ children }) {
  const { teams, currentTeam, loading, permissions, selectTeam, createTeam } =
    useTeam();
  const { user } = React.useContext(AuthContext);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      alert("Please enter a team name");
      return;
    }

    try {
      setCreating(true);
      await createTeam(teamName, teamDescription);
      setShowCreateModal(false);
      setTeamName("");
      setTeamDescription("");
    } catch (error) {
      console.error("Error creating team:", error);
      alert("Failed to create team");
    } finally {
      setCreating(false);
    }
  };

  if (!user) {
    return (
      <div className="data-next-auth-required">
        <div className="auth-message">
          <h2>Authentication Required</h2>
          <p>Please log in to access Data-Next</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="data-next-loading">
        <div className="spinner"></div>
        <p>Loading teams...</p>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="data-next-no-teams">
        <div className="no-teams-content">
          <div className="icon">📊</div>
          <h2>Welcome to Data-Next!</h2>
          <p>
            Create your first team to get started with AI-powered diagram
            generation
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            Create Your First Team
          </button>

          {showCreateModal && (
            <div
              className="modal-overlay"
              onClick={() => setShowCreateModal(false)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <h3>Create New Team</h3>
                <input
                  type="text"
                  placeholder="Team Name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="input"
                />
                <textarea
                  placeholder="Team Description (optional)"
                  value={teamDescription}
                  onChange={(e) => setTeamDescription(e.target.value)}
                  className="textarea"
                  rows={3}
                />
                <div className="modal-actions">
                  <button
                    onClick={handleCreateTeam}
                    disabled={creating || !teamName.trim()}
                    className="btn btn-primary"
                  >
                    {creating ? "Creating..." : "Create Team"}
                  </button>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="data-next-team-selector">
        <div className="team-selector-content">
          <h2>Select a Team</h2>
          <p>Choose which team you want to work with</p>
          <div className="team-list">
            {teams.map((team) => (
              <div
                key={team.id}
                className="team-card"
                onClick={() => selectTeam(team.id)}
              >
                <h3>{team.teamName}</h3>
                {team.description && <p>{team.description}</p>}
                <div className="team-role">
                  {team.permissions.isOwner && (
                    <span className="badge badge-owner">Owner</span>
                  )}
                  {team.permissions.isCreator && !team.permissions.isOwner && (
                    <span className="badge badge-creator">Creator</span>
                  )}
                  {team.permissions.isReader &&
                    !team.permissions.isCreator &&
                    !team.permissions.isOwner && (
                      <span className="badge badge-reader">Reader</span>
                    )}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-secondary"
          >
            + Create New Team
          </button>

          {showCreateModal && (
            <div
              className="modal-overlay"
              onClick={() => setShowCreateModal(false)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <h3>Create New Team</h3>
                <input
                  type="text"
                  placeholder="Team Name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="input"
                />
                <textarea
                  placeholder="Team Description (optional)"
                  value={teamDescription}
                  onChange={(e) => setTeamDescription(e.target.value)}
                  className="textarea"
                  rows={3}
                />
                <div className="modal-actions">
                  <button
                    onClick={handleCreateTeam}
                    disabled={creating || !teamName.trim()}
                    className="btn btn-primary"
                  >
                    {creating ? "Creating..." : "Create Team"}
                  </button>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!permissions.canRead) {
    return (
      <div className="data-next-access-denied">
        <div className="access-denied-content">
          <div className="icon">🔒</div>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this team</p>
          <button onClick={() => selectTeam(null)} className="btn btn-primary">
            Choose Different Team
          </button>
        </div>
      </div>
    );
  }

  return children;
}
